import React, { useState, useEffect } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { chain } from 'lodash';
import { getYear, startOfYear, endOfYear, subYears, isAfter, isBefore, isEqual } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Collapse,
  Box,
  Typography,
  Link,
} from '@mui/material';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import theme from 'theme';
import { KeyboardArrowUp, KeyboardArrowDown, AccessTime, Done } from '@mui/icons-material';
import { BudgetCosts, InvoiceUpload } from 'components/BudgetCosts';
import { apiClient } from '../../../api-client/index.js';
import { Portlet, PortletHeader, PortletLabel, PortletContent } from '../../../components/Portlets';
import { Status } from '../../../components/Status';
import { DarkTooltip } from '../../../components/DarkTooltip';
import { formatDate, snapToArray, isHttpUrl } from '../../../utils';
import { STATUS_COLORS } from '../../../constants/requestStatus';
import { useFirestore } from '../../../utils/firebase';
import { useAuth } from '../../../auth';
import RequestAction from './RequestAction';
import Controls from './Controls';
import { InlineEdit } from '../../../components/InlineEdit';

const eduBudgetCollection = 'edu_budget_requests';

const Row = props => {
  const {
    status,
    requestedAt,
    type,
    title,
    link,
    description,
    requestId,
    onCancelRequestAction,
    onDenyRequestAction,
    onApproveRequestAction,
    onCompleteRequestAction,
    userId,
    totalAmount,
    requestedBy,
    filteredRequests,
    updateRequests,
    userAvailableEduBudget,
    hideActionsColumnAndCell,
    canActOnRequest,
    startDate,
    endDate,
    budgetCosts,
  } = props;

  const [open, setOpen] = React.useState(false);
  const { isHardwareManager, getToken } = useAuth();

  const showAttachment =
    (status === 'approved' || status === 'completed' || status === 'canceled') &&
    !(type === 'Conference' || type === 'Workshop');

  const formatRequestedBy = ({ firstName, lastName } = {}) =>
    firstName && lastName ? (
      <div>
        {firstName} {lastName}
      </div>
    ) : (
      <div>{firstName || lastName}</div>
    );

  const updateEduBudgetRequests = async (id, data) => {
    const result = await apiClient(
      'POST',
      `/requests/edu-budget/${id}/total-amount`,
      getToken,
      data,
    );
    if (result?.status !== 'OK') {
      throw new Error(result?.message || 'Request failed');
    }
    updateRequests({ requestId: id, ...data });
  };

  return (
    <>
      <TableRow sx={{ boxShadow: 'none' }} data-cy={description} hover>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none', width: '1rem' }}>
          <IconButton size="small" onClick={() => setOpen(!open)} data-cy="expandRow">
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
            <Status
              sx={{ marginRight: theme.spacing(1) }}
              color={STATUS_COLORS[status]}
              size="sm"
            />
            {status}
          </Box>
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          {formatRequestedBy(requestedBy)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>{type}</TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          {formatDate(requestedAt)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>{title}</TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          <Box
            component="div"
            sx={{ display: 'flex', alignItems: 'center', width: '6rem', height: '3rem' }}
          >
            {(status === 'pending' || status === 'approved') && (
              <DarkTooltip
                title="Preliminary amount - may differ until the 
              request is completed"
              >
                <AccessTime
                  sx={{
                    color: '#686868',
                    marginRight: '0.5rem',
                    width: '1.2rem',
                    height: '1.2rem',
                  }}
                />
              </DarkTooltip>
            )}
            {status === 'completed' && (
              <Done
                sx={{ color: '#65B32E', marginRight: '0.5rem', width: '1.2rem', height: '1.2rem' }}
              />
            )}

            {(status === 'pending' || status === 'approved') && !isHardwareManager ? (
              <InlineEdit
                id={requestId}
                name="totalAmount"
                value={totalAmount || 0}
                onUpdate={updateEduBudgetRequests}
                tooltipTitle="Click to edit."
                min={0}
              />
            ) : (
              totalAmount
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          <DarkTooltip title="The available education budget at the moment of request creation.">
            <span>{userAvailableEduBudget}</span>
          </DarkTooltip>
        </TableCell>

        {hideActionsColumnAndCell(filteredRequests) && (
          <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none', minWidth: '10rem' }}>
            {canActOnRequest(userId, status) ? (
              <RequestAction
                userId={userId}
                requestId={requestId}
                status={status}
                type={type}
                title={title}
                link={link}
                totalAmount={totalAmount}
                requestedBy={requestedBy}
                onApproveRequestAction={onApproveRequestAction}
                onDenyRequestAction={onDenyRequestAction}
                onCancelRequestAction={onCancelRequestAction}
                onCompleteRequestAction={onCompleteRequestAction}
                description={description}
              />
            ) : (
              <div />
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow
        sx={{
          height: '0px',
          padding: 0,
          borderBottom: 'unset',
          borderTop: 'unset',
          boxShadow: 'none',
        }}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box
                sx={[
                  showAttachment
                    ? { display: 'flex', flexDirection: 'row', width: '100%' }
                    : { width: '100%', marginRight: '2rem' },
                ]}
              >
                {!showAttachment && (
                  <Typography variant="subtitle1" sx={{ marginBottom: '1rem', color: 'black' }}>
                    Request Details
                  </Typography>
                )}

                <Box
                  sx={{
                    marginTop: '1.5rem',
                    marginBottom: '2rem',
                    marginRight: '1rem',
                    padding: '1rem',
                    border: `1px solid ${theme.palette.border}`,
                    width: '100%',
                  }}
                >
                  {showAttachment && (
                    <Typography variant="subtitle1" sx={{ marginBottom: '1rem', color: 'black' }}>
                      Request Details
                    </Typography>
                  )}

                  {startDate && endDate && (
                    <>
                      <Typography
                        sx={{
                          marginBottom: '0.5rem',
                          color: 'black',
                          fontWeight: '300',
                          fontSize: '0.875rem',
                        }}
                        variant="body1"
                      >
                        Start date / End date
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ marginBottom: '1rem', fontWeight: '400', color: 'black' }}
                      >
                        {formatDate(startDate)} / {formatDate(endDate)}
                      </Typography>
                    </>
                  )}
                  <Typography
                    sx={{
                      marginBottom: '0.5rem',
                      color: 'black',
                      fontWeight: '300',
                      fontSize: '0.875rem',
                    }}
                    variant="body1"
                  >
                    Link
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: '1rem', fontWeight: '400', color: 'black' }}
                  >
                    {isHttpUrl(link) ? (
                      <Link
                        underline="always"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link}
                      </Link>
                    ) : (
                      link
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      marginBottom: '0.5rem',
                      color: 'black',
                      fontWeight: '300',
                      fontSize: '0.875rem',
                    }}
                    variant="body1"
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: '1rem', fontWeight: '400', color: 'black' }}
                  >
                    {description}
                  </Typography>
                </Box>

                {showAttachment && (
                  <Box
                    sx={{
                      marginTop: '1.5rem',
                      padding: '1.5rem 1rem',
                      border: `1px solid ${theme.palette.border}`,
                      width: '30rem',
                      marginBottom: '2rem',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        borderBottom: `1px solid ${theme.palette.border}`,
                        marginBottom: '1rem',
                        paddingBottom: '1rem',
                      }}
                    >
                      Attachment
                    </Typography>

                    {(budgetCosts[0] ? budgetCosts[0].invoiceIds.length === 0 : false) &&
                      status !== 'approved' && (
                        <Typography variant="body2" sx={{ color: ' #979797' }}>
                          No files uploaded
                        </Typography>
                      )}

                    <InvoiceUpload
                      budgetCostName="Bill"
                      eduBudgetRequestId={requestId}
                      status={status}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {(type === 'Conference' || type === 'Workshop') && (
              <BudgetCosts
                budgetCosts={budgetCosts}
                eduBudgetRequestId={requestId}
                eduBudgetRequestStatus={status}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TeamEduBudgetRequestsTable = () => {
  const { user: authenticatedUser, isHardwareManager, isOfficeManager, getToken } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState(['pending', 'approved']);
  const [nameFilter, setNameFilter] = useState('');
  const [eduBudgetPeriod, setEduBudgetPeriod] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const database = useFirestore();

  const filterRequests = requests => {
    if (!requests) {
      return null;
    }

    const filterByName = ({ requestedBy: { firstName, lastName } }) => {
      const fields = [firstName, lastName, `${firstName} ${lastName}`];

      return fields.some(string => string.toLowerCase().startsWith(nameFilter.toLowerCase()));
    };

    const filterByPeriod = ({ requestedAt }) => {
      if (!eduBudgetPeriod) {
        return true;
      }

      const requestedDate = requestedAt?.toDate ? requestedAt.toDate() : new Date(requestedAt);
      const { start, end } = eduBudgetPeriod;

      return (
        isAfter(requestedDate, start) &&
        (isBefore(requestedDate, end) || isEqual(requestedDate, end))
      );
    };

    return chain(requests).filter(filterByName).filter(filterByPeriod).value();
  };

  const getEduBudgetPeriods = years => {
    const year = getYear(new Date());
    const baseStart = startOfYear(new Date(year, 0, 1));
    const baseEnd = endOfYear(new Date(year, 0, 1));

    return Array.from({ length: years }, (_, key) => {
      const start = subYears(baseStart, key);
      const end = subYears(baseEnd, key);
      return { start, end, key };
    });
  };

  const updateRequests = input => {
    setRequests(prev =>
      prev.map(request => (request.id === input.requestId ? { ...request, ...input } : request)),
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const requestEduBudgetStatusUpdate = async (requestId, status, note) => {
    const result = await apiClient('POST', `/requests/edu-budget/${requestId}/status`, getToken, {
      status,
      ...(note !== undefined && { note }),
    });

    if (result?.status !== 'OK') {
      throw new Error(result?.message || 'Request failed');
    }
  };

  const onApproveRequestAction = async ({ requestId, status }) => {
    await requestEduBudgetStatusUpdate(requestId, status);
    updateRequests({ requestId, status });
    return true;
  };

  const onDenyRequestAction = async ({ requestId, status, note }) => {
    await requestEduBudgetStatusUpdate(requestId, status, note);
    updateRequests({ requestId, status });
    return true;
  };

  const onCancelRequestAction = async ({ requestId, status }) => {
    await requestEduBudgetStatusUpdate(requestId, status);
    updateRequests({ requestId, status });
    return true;
  };

  const onCompleteRequestAction = async ({ requestId, status }) => {
    await requestEduBudgetStatusUpdate(requestId, status);
    updateRequests({ requestId, status });
    return true;
  };

  useEffect(() => {
    setLoading(true);

    let queryRequests = query(
      collection(database, eduBudgetCollection),
      where('userTenant', '==', authenticatedUser.tenant.id),
      orderBy('requestedAt', 'desc'),
    );

    if (statusFilter.length !== 0) {
      queryRequests = query(
        collection(database, eduBudgetCollection),
        where('userTenant', '==', authenticatedUser.tenant.id),
        where('status', 'in', statusFilter),
        orderBy('requestedAt', 'desc'),
      );
    }

    getDocs(queryRequests)
      .then(snapshot => {
        setRequests(snapToArray(snapshot));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [database, statusFilter, page, rowsPerPage, authenticatedUser.tenant.id]);

  const filteredRequests = filterRequests(requests);

  const hideActionsColumnAndCell = filteredRequests =>
    !isHardwareManager &&
    filteredRequests.filter(request =>
      isOfficeManager
        ? request.status === 'approved'
        : request.status === 'pending' || request.status === 'approved',
    ).length > 0;

  const { assignedManager } = authenticatedUser;
  const isOwnAssignedManager = Array.isArray(assignedManager)
    ? assignedManager.some(manager => manager.email === authenticatedUser.email)
    : assignedManager?.email === authenticatedUser.email;

  const canActOnRequest = (requestUserId, status) => {
    if (status !== 'pending' && status !== 'approved') return false;
    const isOwnRequest = requestUserId === authenticatedUser.uid;
    return !isOwnRequest || isOwnAssignedManager;
  };

  return (
    <Portlet
      sx={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
      outlined={1}
      squared={false}
    >
      <PortletHeader
        sx={{
          '@media (max-width:480px)': {
            display: 'table',
            paddingBottom: '1.5rem',
          },
        }}
        noDivider
      >
        <PortletLabel
          subtitle={`${filteredRequests.length} in total`}
          title="Latest Requests"
          sx={{
            '@media (max-width:480px)': {
              '& h5': {
                fontSize: '1.3rem',
              },
              '& h6': {
                marginLeft: '0.5rem',
                fontSize: '0.7rem',
                width: '3rem',
              },
            },
          }}
        />
        <Controls
          periods={getEduBudgetPeriods(2)}
          statusFilter={statusFilter}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          setStatusFilter={setStatusFilter}
          eduBudgetPeriod={eduBudgetPeriod}
          setEduBudgetPeriod={setEduBudgetPeriod}
          setPage={setPage}
        />
      </PortletHeader>

      <PerfectScrollbar>
        <PortletContent isLoading={isLoading} noPadding>
          {filteredRequests.length ? (
            <>
              <Table stickyHeader style={{ borderSpacing: '0px 0px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Status</TableCell>
                    <TableCell>Requested by</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Total Amount (EUR)</TableCell>
                    <TableCell>
                      <DarkTooltip title="The available education budget at the moment of request creation.">
                        <span>Available Edu Budget (EUR)</span>
                      </DarkTooltip>
                    </TableCell>

                    {(['pending', 'all', 'approved', 'completed'].some(item =>
                      statusFilter.includes(item),
                    ) ||
                      statusFilter.length === 0) &&
                      hideActionsColumnAndCell(filteredRequests) && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRequests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                      ({
                        id: requestId,
                        userId,
                        requestedBy = {},
                        description,
                        title,
                        type,
                        link,
                        requestedAt,
                        totalAmount = {},
                        status = 'pending',
                        userAvailableEduBudget,
                        startDate,
                        endDate,
                        budgetCosts,
                      }) => (
                        <Row
                          requestId={requestId}
                          userId={userId}
                          description={description}
                          requestedBy={requestedBy}
                          type={type}
                          title={title}
                          link={link}
                          requestedAt={requestedAt}
                          status={status}
                          totalAmount={totalAmount}
                          userAvailableEduBudget={userAvailableEduBudget}
                          onCancelRequestAction={onCancelRequestAction}
                          onApproveRequestAction={onApproveRequestAction}
                          onDenyRequestAction={onDenyRequestAction}
                          onCompleteRequestAction={onCompleteRequestAction}
                          updateRequests={updateRequests}
                          filteredRequests={filteredRequests}
                          hideActionsColumnAndCell={hideActionsColumnAndCell}
                          canActOnRequest={canActOnRequest}
                          startDate={startDate}
                          endDate={endDate}
                          key={requestId}
                          budgetCosts={budgetCosts}
                        />
                      ),
                    )}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={filteredRequests.length}
                page={page}
              />
            </>
          ) : (
            <Box
              component="div"
              sx={{ padding: 24, paddingTop: 0, fontWeight: 300, color: '#777' }}
            >
              No requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
    </Portlet>
  );
};

export default TeamEduBudgetRequestsTable;
